import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Card,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Alert,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import dataConstant from "../../constants/dataConstants";
import urlConstant from "../../constants/urlConstants";
import { httpGet } from "../../services/commonService";
import useDebounce from "../custom-hook/useDebounce";

const CustomTextFieldLeft = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "var(--inputs)",
    color: "var(--black)",
    fontSize: "0.9rem",
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    minHeight: "40px",
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "1rem",
    color: "var(--black)",
    marginBottom: theme.spacing(0.5),
  },
}));

const Resources = ({ onViewAll, onViewGalleryMedia }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mediaTitleSearch, setMediaTitleSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const { accessToken, impersonateEmail } = useContext(AuthenticationContext);
  const [decodedId, setDecodedId] = useState(null);
  const [mediaMenuItems, setMediaMenuItems] = useState([]);
  const { t } = useTranslation();
  const debouncedSearch = useDebounce(mediaTitleSearch, 500);
  const [isValidId, IsValidMediaId] = useState(false);

  useEffect(() => {
    try {
      const decoded = atob(id);
      setDecodedId(decoded);
      const decodedNumber = Number(decoded);
      if (Number.isInteger(decodedNumber)) {
        IsValidMediaId(true);
      } else {
        navigate("/mediaPage");
      }
    } catch (err) {
      navigate("/mediaPage");
    }
  }, [id, navigate]);

  const url = `${urlConstant.MediaPage.GetAllMedialMenuById}?Id=${decodedId}&Search=${mediaTitleSearch}`;

  const getResources = async () => {
    setLoading(true);
    await httpGet(url, true, true, accessToken, impersonateEmail)
      .then((response) => {
        const responseData = response.Value.Data;
        setMediaMenuItems(responseData);        
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!isValidId) return;
    getResources();
  }, [debouncedSearch, decodedId]);

  const handleSearchChange = (event) => {
    setMediaTitleSearch(event.target.value);
  };
  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"}>
        <Box width={"100%"}>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              border: "1px solid var(--headerbg)",
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap={"20px"}>
              <Stack
                direction={"row"}
                gap={1}
                width={"100%"}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent={"space-between"}
                flexWrap={"wrap"}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <Typography
                  color="secondary.light"
                  variant="h4"
                  fontWeight={600}
                >
                  {t("Media.Resources")}
                </Typography>
                <Stack
                  className="customfield-label-left cm-normal-search"
                  sx={{
                    maxWidth: "500px",
                    width: "100%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                  }}
                >
                  <Card className="customfield-label-inner">
                    <SearchOutlinedIcon />
                  </Card>
                  <CustomTextFieldLeft
                    fullWidth
                    placeholder={t("Common.Search")}
                    variant="outlined"
                    value={mediaTitleSearch}
                    onChange={handleSearchChange}
                  />
                </Stack>
              </Stack>
              <Grid container spacing={3} sx={{ flexGrow: 1 }}>
                {!loading && mediaMenuItems?.length === 0 && (
                  <Alert
                    sx={(theme) => ({
                      background: theme.palette.common.errorbg,
                      color: theme.palette.common.errortext,
                      marginBottom: "5px",
                      width: "100%",
                    })}
                    variant="filled"
                    severity="info"
                  >
                    {t("Media.No media available")}
                  </Alert>
                )}
                {mediaMenuItems?.map((mediaItem) => {
                  return (
                    <Grid
                      key={mediaItem.Id}
                      size={{ xs: 12, md: 6, lg: 6, xxl: 4 }}
                    >
                      <Card
                        sx={(theme) => ({
                          p: {
                            xs: "20px",
                            sm: "25px 30px",
                          },
                          background: theme.palette.secondary.lightest,
                          borderRadius: "10px",
                          height: "100%",
                        })}
                      >
                        <Stack textAlign={"center"}>
                          <Card
                            className="bg-dark"
                            sx={{
                              py: 1,
                              px: 2,
                              textAlign: "center",
                              width: {
                                xs: "100%",
                                xxl: "auto",
                              },
                              mb: "10px",
                            }}
                          >
                            <Typography
                              color="primary"
                              sx={{ wordBreak: "break-word" }}
                              variant="h4"
                            >
                              {mediaItem.Title}
                            </Typography>
                          </Card>
                        </Stack>
                        <TableContainer
                          sx={{ background: "transparent", boxShadow: "none" }}
                          component={Paper}
                        >
                          <Table
                            sx={{ background: "transparent" }}
                            aria-label="simple table"
                          >
                            <TableBody>
                              <TableRow
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                  "& td,& th": {
                                    borderBottom:
                                      "1px solid var(--black) !important",
                                    paddingLeft: "0px",
                                    paddingRight: "0px",
                                    paddingTop: "7px",
                                    paddingBottom: "7px",
                                  },
                                }}
                              >
                                <TableCell>
                                  <Stack
                                    direction={"row"}
                                    alignItems={"center"}
                                    gap={1}
                                  >
                                    {" "}
                                    <Typography
                                      color="common.white"
                                      sx={{}}
                                      variant="body1"
                                      fontWeight={600}
                                    >
                                      {mediaItem.TextBlogCount}
                                    </Typography>
                                    <Typography
                                      color="secondary.text"
                                      sx={{ fontSize: "12px" }}
                                      variant="body2"
                                    >
                                      {t("Media.TextBlog")}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell valign="middle" align="right">
                                  <Stack
                                    direction={"row"}
                                    gap={1}
                                    alignItems={"center"}
                                    display={"inline-flex"}
                                  >
                                    <Button
                                      variant="contained"
                                      color="secondary"
                                      sx={{
                                        px: 1,
                                        py: 1,
                                        margin: 0,
                                        "& .MuiButton-startIcon": {
                                          margin: 0,
                                        },
                                      }}
                                      startIcon={<VisibilityOutlinedIcon />}
                                      onClick={() =>
                                        onViewGalleryMedia(
                                          mediaItem.Id,
                                          mediaItem.MediaId,
                                          dataConstant.MediaCategoryTypes
                                            .TextBlog
                                        )
                                      }
                                      disabled={mediaItem.TextBlogCount === 0}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                              <TableRow
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                  "& td,& th": {
                                    borderBottom:
                                      "1px solid var(--black) !important",
                                    paddingLeft: "0px",
                                    paddingRight: "0px",
                                    paddingTop: "7px",
                                    paddingBottom: "7px",
                                  },
                                }}
                              >
                                <TableCell>
                                  <Stack
                                    direction={"row"}
                                    alignItems={"center"}
                                    gap={1}
                                  >
                                    {" "}
                                    <Typography
                                      color="common.white"
                                      sx={{}}
                                      variant="body1"
                                      fontWeight={600}
                                    >
                                      {mediaItem.VideoCount}
                                    </Typography>
                                    <Typography
                                      color="secondary.text"
                                      sx={{ fontSize: "12px" }}
                                      variant="body2"
                                    >
                                      {t("Media.Videos")}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell valign="middle" align="right">
                                  <Stack
                                    direction={"row"}
                                    gap={1}
                                    alignItems={"center"}
                                    display={"inline-flex"}
                                  >
                                    <Button
                                      variant="contained"
                                      color="secondary"
                                      sx={{
                                        px: 1,
                                        py: 1,
                                        margin: 0,
                                        "& .MuiButton-startIcon": {
                                          margin: 0,
                                        },
                                      }}
                                      startIcon={<VisibilityOutlinedIcon />}
                                      onClick={() =>
                                        onViewGalleryMedia(
                                          mediaItem.Id,
                                          mediaItem.MediaId,
                                          dataConstant.MediaCategoryTypes.Video
                                        )
                                      }
                                      disabled={mediaItem.VideoCount === 0}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                              <TableRow
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                  "& td,& th": {
                                    borderBottom:
                                      "1px solid var(--black) !important",
                                    paddingLeft: "0px",
                                    paddingRight: "0px",
                                    paddingTop: "7px",
                                    paddingBottom: "7px",
                                  },
                                }}
                              >
                                <TableCell>
                                  <Stack
                                    direction={"row"}
                                    alignItems={"center"}
                                    gap={1}
                                  >
                                    {" "}
                                    <Typography
                                      color="common.white"
                                      sx={{}}
                                      variant="body1"
                                      fontWeight={600}
                                    >
                                      {mediaItem.PictureCount}
                                    </Typography>
                                    <Typography
                                      color="secondary.text"
                                      sx={{ fontSize: "12px" }}
                                      variant="body2"
                                    >
                                      {t("Media.Images")}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell valign="middle" align="right">
                                  <Stack
                                    direction={"row"}
                                    gap={1}
                                    alignItems={"center"}
                                    display={"inline-flex"}
                                  >
                                    <Button
                                      variant="contained"
                                      color="secondary"
                                      sx={{
                                        px: 1,
                                        py: 1,
                                        margin: 0,
                                        "& .MuiButton-startIcon": {
                                          margin: 0,
                                        },
                                      }}
                                      startIcon={<VisibilityOutlinedIcon />}
                                      onClick={() =>
                                        onViewGalleryMedia(
                                          mediaItem.Id,
                                          mediaItem.MediaId,
                                          dataConstant.MediaCategoryTypes
                                            .Picture
                                        )
                                      }
                                      disabled={mediaItem.PictureCount === 0}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                              <TableRow
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                  "& td,& th": {
                                    borderBottom:
                                      "1px solid var(--black) !important",
                                    paddingLeft: "0px",
                                    paddingRight: "0px",
                                    paddingTop: "7px",
                                    paddingBottom: "7px",
                                  },
                                }}
                              >
                                <TableCell>
                                  <Stack
                                    direction={"row"}
                                    alignItems={"center"}
                                    gap={1}
                                  >
                                    {" "}
                                    <Typography
                                      color="common.white"
                                      sx={{}}
                                      variant="body1"
                                      fontWeight={600}
                                    >
                                      {mediaItem.PdfCount}
                                    </Typography>
                                    <Typography
                                      color="secondary.text"
                                      sx={{ fontSize: "12px" }}
                                      variant="body2"
                                    >
                                      {t("Media.PDF")}
                                    </Typography>
                                  </Stack>
                                </TableCell>
                                <TableCell valign="middle" align="right">
                                  <Stack
                                    direction={"row"}
                                    gap={1}
                                    alignItems={"center"}
                                    display={"inline-flex"}
                                  >
                                    <Button
                                      variant="contained"
                                      color="secondary"
                                      sx={{
                                        px: 1,
                                        py: 1,
                                        margin: 0,
                                        "& .MuiButton-startIcon": {
                                          margin: 0,
                                        },
                                      }}
                                      startIcon={<VisibilityOutlinedIcon />}
                                      onClick={() =>
                                        onViewGalleryMedia(
                                          mediaItem.Id,
                                          mediaItem.MediaId,
                                          dataConstant.MediaCategoryTypes.Pdf
                                        )
                                      }
                                      disabled={mediaItem.PdfCount === 0}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Stack
                          direction={"row"}
                          mt={2}
                          sx={{ justifyContent: "center" }}
                        >
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<VisibilityOutlinedIcon />}
                            sx={{ width: { xs: "100%", sm: "auto" } }}
                            onClick={() => onViewAll(mediaItem.Id)}
                          >
                            {t("Media.View All")}
                          </Button>
                        </Stack>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Stack>
          </Card>
        </Box>
      </Box>
    </>
  );
};
export default Resources;
